import { motion } from "framer-motion";

/**
 * Atmospheric misty brand-coloured swirl behind page content.
 * Very soft, slow, large — meant to feel like fog, not accents.
 */
export default function AnimatedBackground({ variant = "default" }) {
  // 4 very soft, oversized blobs per variant — different seeds for each page.
  const configs = {
    default: [
      { color: "#0066FF", size: 1100, from: { x: "-30%", y: "-15%" }, to: { x: "10%", y: "25%" }, dur: 42 },
      { color: "#FF007F", size: 1000, from: { x: "70%", y: "80%" }, to: { x: "45%", y: "50%" }, dur: 48 },
      { color: "#0066FF", size: 900,  from: { x: "60%", y: "-10%" }, to: { x: "40%", y: "15%" }, dur: 55 },
      { color: "#FF007F", size: 850,  from: { x: "-20%", y: "60%" }, to: { x: "5%", y: "80%" }, dur: 60 },
    ],
    cool: [
      { color: "#0066FF", size: 1200, from: { x: "60%", y: "-20%" }, to: { x: "30%", y: "10%" }, dur: 50 },
      { color: "#4d9bff", size: 950,  from: { x: "-25%", y: "40%" }, to: { x: "15%", y: "60%" }, dur: 44 },
      { color: "#FF007F", size: 800,  from: { x: "70%", y: "70%" }, to: { x: "90%", y: "40%" }, dur: 58 },
      { color: "#0066FF", size: 900,  from: { x: "20%", y: "90%" }, to: { x: "40%", y: "65%" }, dur: 52 },
    ],
    warm: [
      { color: "#FF007F", size: 1100, from: { x: "-15%", y: "0%" }, to: { x: "20%", y: "30%" }, dur: 48 },
      { color: "#0066FF", size: 1000, from: { x: "75%", y: "70%" }, to: { x: "55%", y: "45%" }, dur: 54 },
      { color: "#FF007F", size: 900,  from: { x: "50%", y: "-15%" }, to: { x: "70%", y: "10%" }, dur: 60 },
      { color: "#4d9bff", size: 820,  from: { x: "-10%", y: "80%" }, to: { x: "15%", y: "95%" }, dur: 46 },
    ],
    editorial: [
      { color: "#0066FF", size: 1150, from: { x: "55%", y: "0%" }, to: { x: "80%", y: "25%" }, dur: 55 },
      { color: "#FF007F", size: 950,  from: { x: "-15%", y: "55%" }, to: { x: "20%", y: "35%" }, dur: 50 },
      { color: "#0066FF", size: 880,  from: { x: "30%", y: "85%" }, to: { x: "10%", y: "60%" }, dur: 46 },
      { color: "#FF007F", size: 820,  from: { x: "80%", y: "80%" }, to: { x: "60%", y: "55%" }, dur: 58 },
    ],
  };
  const orbs = configs[variant] || configs.default;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      data-testid={`bg-anim-${variant}`}
    >
      {orbs.map((o, i) => (
        <motion.span
          key={i}
          initial={{ left: o.from.x, top: o.from.y, scale: 1 }}
          animate={{
            left: [o.from.x, o.to.x, o.from.x],
            top: [o.from.y, o.to.y, o.from.y],
            scale: [1, 1.15, 0.95, 1],
            opacity: [0.08, 0.14, 0.1, 0.08],
          }}
          transition={{ duration: o.dur, repeat: Infinity, ease: "easeInOut" }}
          style={{
            width: o.size,
            height: o.size,
            background: `radial-gradient(circle, ${o.color} 0%, transparent 65%)`,
            filter: "blur(100px)",
          }}
          className="absolute -translate-x-1/2 -translate-y-1/2"
        />
      ))}
      {/* ambient wash to tie everything together */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 0%, rgba(2,6,17,0.6) 90%)" }} />
    </div>
  );
}
